import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AxiosResponse } from 'axios';
import { useForm } from 'react-hook-form';

import {
  CompanyRoleDTO,
  InviteModulePermissionOverride,
  ModulePermissionOverridesDTO,
  PermissionRequirementPair,
  SendStaffInviteRequest,
  UpdateModulePermissionOverridePayload,
  UpdateStaffPermissionsPayload,
} from '@vyce/core/src/api/types';
import { DeviceContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { RolesModuleContext } from '@vyce/core/src/contexts/rolesModuleProvider';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import {
  invalidateCompanyRolesData,
  useCompanyRolesData,
} from '@vyce/core/src/modules/rolesModule/hooks/useCompanyRolesData';
import { usePermissionCatalogue } from '@vyce/core/src/modules/rolesModule/hooks/usePermissionCatalogue';
import { ModulePermissionState, VisibleStaffModule } from '@vyce/core/src/modules/rolesModule/types';
import {
  getSaveErrorNotification,
  parsePermissionConflicts,
} from '@vyce/core/src/modules/rolesModule/utils/permissionErrors';
import {
  buildCatalogueIndex,
  CatalogueIndex,
  closure,
  explicitFromEffective,
  moduleRoleDefaults,
  ownPermissions,
  permissionStateFromDTO,
  setsEqual,
  toDisplayName,
} from '@vyce/core/src/modules/rolesModule/utils/permissionState';
import {
  applyRoleReassignments,
  buildRolesPayload,
  buildVisibleModules,
  CompanyDraft,
  draftFromStaffItem,
  findStaffMember,
  getDraftInvalidReason,
  getRoleReassignments,
} from '@vyce/core/src/modules/rolesModule/utils/roleDraft';
import { CompanyWorkerItem, StaffListItem } from '@vyce/core/src/types';
import { requiresResourceSelection, toggleId } from '@vyce/core/src/utils/staffPermissions';

import { AdminInvitePrefill, adminInvitePrefill } from '../prefill';

export type AdminInviteStep = 'who' | 'role';

export const NEW_ADMIN_TAB = 0;
export const EXISTING_USER_TAB = 1;

type ModulesStatus = 'idle' | 'loading' | 'ready' | 'error';
type InviteRowsStatus = 'idle' | 'loading' | 'ready' | 'error';

// How long the ticks have to sit still before the invite's own checks run on them
const INVITE_CHECK_DELAY_MS = 500;

// IAM's 422s carry their reason in the title
const errorTitle = (e: any): string => e?.response?.data?.detail?.message?.title || '';
const errorDescription = (e: any): string => {
  const detail = e?.response?.data?.detail;
  return detail?.message?.description || detail?.description || (typeof detail === 'string' ? detail : '');
};
const isInvalidRoleError = (e: any) => /invalid role/i.test(errorTitle(e));
const isUnavailableModuleError = (e: any) => /module not available/i.test(errorTitle(e));

/** A module's own permissions, for the rows the auto-added names are shown against. */
export interface AutoAddedRow {
  systemName: string;
  label: string;
  permissions: string[];
}

interface Form {
  email: string;
}

type StaffRow = StaffListItem & { position_name?: string };

// Per module; a module is missing when it has no role, or its role doesn't say
// what it grants — there is nothing to customise against either way.
type PermissionDraft = Record<string, ModulePermissionState>;

// The owner role short-circuits every check on the backend, and IAM refuses
// overrides on an owner, so there is nothing to fine-tune.
const isFullAccessRole = (role: CompanyRoleDTO) =>
  /owner/i.test(role.role_name) ||
  !!role.permission_names?.some(name => name.trim().toLowerCase() === 'full access');

interface Options {
  open: boolean;
  companyId: string | null;
  onClose: () => void;
  onSuccess: () => void;
  updateStaffPermissionsRequest: (
    companyId: string,
    userId: string,
    data: UpdateStaffPermissionsPayload
  ) => Promise<AxiosResponse<unknown>>;
  createStaffInviteRequest: (
    companyId: string,
    payload: SendStaffInviteRequest
  ) => Promise<AxiosResponse<unknown>>;
}

/**
 * Brain of the Add Admin dialog: who (a new email or one of the company's existing
 * people), then which role in each module. Existing people are given the roles
 * straight away; new ones get an email invite carrying the same roles.
 */
// A prepared invite's roles as the dialog's draft: a module whose role it names
// gets that role and the resources it picked, if they still exist.
const draftFromPrefill = (modules: VisibleStaffModule[], prefill: AdminInvitePrefill): CompanyDraft => {
  const draft: CompanyDraft = {};
  modules.forEach(module => {
    const asked = prefill.roles.find(item => module.roles.some(role => role.role_id === item.roleId));
    const role = asked ? module.roles.find(item => item.role_id === asked.roleId) || null : null;
    draft[module.systemName] = {
      role,
      resourceIds:
        role && asked && requiresResourceSelection(role)
          ? asked.resourceIds.filter(id => module.resources.some(resource => resource.id === id))
          : [],
    };
  });
  return draft;
};

export const useAdminInviteDialog = ({
  open,
  companyId: initialCompanyId,
  onClose,
  onSuccess,
  updateStaffPermissionsRequest,
  createStaffInviteRequest,
}: Options) => {
  const { isMobile } = useContext(DeviceContext);
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const {
    getStaffRequest,
    getUserPermissionOverridesRequest,
    updateUserPermissionOverridesRequest,
    previewPermissionOverridesRequest,
  } = useContext(RolesModuleContext);
  const { fetchCompanyRolesData } = useCompanyRolesData();
  const { fetchCatalogue } = usePermissionCatalogue();
  const { isInternalAdmin } = usePermissions();

  const methods = useForm<Form>({ mode: 'all', defaultValues: { email: '' } });
  const { reset, watch, formState } = methods;
  const email = (watch('email') || '').trim();

  const [companyId, setCompanyId] = useState<string | null>(initialCompanyId);
  const [step, setStep] = useState<AdminInviteStep>('who');
  const [tab, setTab] = useState<number>(NEW_ADMIN_TAB);
  const [pickedUser, setPickedUser] = useState<CompanyWorkerItem | null>(null);
  // The picked person's current staff row — set means we are editing roles they already hold
  const [staffItem, setStaffItem] = useState<StaffRow | null>(null);
  const [position, setPosition] = useState<string>('');
  const [draft, setDraft] = useState<CompanyDraft>({});
  const [modules, setModules] = useState<VisibleStaffModule[]>([]);
  const [catalogue, setCatalogue] = useState<CatalogueIndex | null>(null);
  const [modulesStatus, setModulesStatus] = useState<ModulesStatus>('idle');
  const [permissions, setPermissions] = useState<PermissionDraft>({});
  // What step 2 opened with: the person's current roles and permission state. Edits
  // are diffed against it, so only what actually changed is saved.
  const [baseline, setBaseline] = useState<{ draft: CompanyDraft; permissions: PermissionDraft }>({
    draft: {},
    permissions: {},
  });
  // Modules whose permission list is open under the role chip
  const [customising, setCustomising] = useState<Record<string, boolean>>({});
  // Email invites only. Customise is the server's form there: once it is opened the
  // rows come from the preview for the roles picked, and the invite carries
  // permission_overrides. Never opened means no field — role defaults.
  const [customiseOpened, setCustomiseOpened] = useState<boolean>(false);
  const [inviteRows, setInviteRows] = useState<ModulePermissionOverridesDTO[] | null>(null);
  const [inviteRowsStatus, setInviteRowsStatus] = useState<InviteRowsStatus>('idle');
  const [inviteRowsReload, setInviteRowsReload] = useState<number>(0);
  // What the invite would store for the ticks as they stood (`key`), auto-added
  // dependencies included
  const [inviteCheck, setInviteCheck] = useState<{
    key: string;
    rows: ModulePermissionOverridesDTO[];
  } | null>(null);
  const [inviteError, setInviteError] = useState<string>('');
  const inviteRowsRequestRef = useRef(0);
  const inviteCheckRequestRef = useRef(0);

  const resetInviteCustomise = () => {
    setCustomiseOpened(false);
    setInviteRows(null);
    setInviteRowsStatus('idle');
    setInviteCheck(null);
    setInviteError('');
  };
  const [checkingStaff, setCheckingStaff] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  // Step 2 is only re-seeded when the person changes, so Back → Next keeps the picks
  const seededForRef = useRef<string | null>(null);
  // An invite prepared elsewhere (a Laila card): its email is typed in, then its
  // position and roles fill step 2
  const [prefill, setPrefill] = useState<AdminInvitePrefill | null>(null);

  useEffect(() => {
    setCompanyId(initialCompanyId);
  }, [initialCompanyId]);

  const resetState = useCallback(() => {
    reset({ email: '' });
    setStep('who');
    setTab(NEW_ADMIN_TAB);
    setPickedUser(null);
    setStaffItem(null);
    setPosition('');
    setDraft({});
    setPrefill(null);
    setPermissions({});
    setBaseline({ draft: {}, permissions: {} });
    setCustomising({});
    resetInviteCustomise();
    seededForRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  // A different company means different people, roles and scopes
  useEffect(() => {
    resetState();
  }, [companyId, resetState]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const next = adminInvitePrefill.take();
    if (next) {
      reset({ email: next.email });
      setTab(NEW_ADMIN_TAB);
      setPrefill(next);
    }
  }, [open, reset]);

  // Roles, inventory and catalogue are cached per company, so this is free after the first open
  const loadModules = useCallback(async () => {
    if (!companyId) return;
    try {
      setModulesStatus('loading');
      const [companyData, catalogueModules] = await Promise.all([
        fetchCompanyRolesData(companyId),
        fetchCatalogue(companyId),
      ]);
      const catalogueIndex = buildCatalogueIndex(catalogueModules);
      const visibleModules = buildVisibleModules({
        catalogueModules,
        catalogueIndex,
        rolesData: companyData.roles,
        moduleResources: companyData.resources,
        readable: companyData.readable,
        isInternalAdmin,
      });
      setCatalogue(catalogueIndex);
      setModules(visibleModules);
      setModulesStatus('ready');
      return visibleModules;
    } catch (e) {
      setModulesStatus('error');
      handleServerError(e);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, fetchCompanyRolesData, fetchCatalogue, isInternalAdmin]);

  useEffect(() => {
    if (open && companyId) loadModules();
  }, [open, companyId, loadModules]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const isExisting = tab === EXISTING_USER_TAB;
  // An owner skips every check, so there is nothing to customise — and IAM refuses
  // overrides next to an owner role
  const hasOwnerRole = Object.values(draft).some(item => !!item?.role && isFullAccessRole(item.role));
  // Someone already here gets their overrides saved against their user id once the
  // roles land; an email invite carries them, checked through the preview
  const canCustomise = !hasOwnerRole && (isExisting || !!previewPermissionOverridesRequest);
  const customisesInvite = canCustomise && !isExisting;

  // The preview reads role ids only — scope decides where a permission applies, not which
  const roleIds = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(draft)
            .map(item => item?.role?.role_id)
            .filter(Boolean) as string[]
        )
      ).sort(),
    [draft]
  );
  const rolesKey = roleIds.join(',');

  // The ticks a role starts from, or nothing when the role can't be customised
  const permissionStateForRole = (module: VisibleStaffModule, role: CompanyRoleDTO | null) => {
    if (!role || !catalogue || isFullAccessRole(role)) return undefined;
    const defaults = moduleRoleDefaults(role, module, catalogue);
    if (!defaults) return undefined;
    const state: ModulePermissionState = {
      roleDefaults: defaults,
      added: [],
      removed: [],
      baselineEffective: defaults,
      explicit: Array.from(explicitFromEffective(defaults, catalogue.index)),
      clearResourceOverrides: true,
      conflicts: [],
      hasOverridesRow: false,
    };
    return state;
  };

  // 404 means the person holds no position in this company yet — nothing to seed from
  const loadOverrides = async (id: string, userId: string): Promise<ModulePermissionOverridesDTO[]> => {
    try {
      const res = await getUserPermissionOverridesRequest(id, userId);
      return res.data || [];
    } catch (e: any) {
      if (e?.response?.status === 404) return [];
      throw e;
    }
  };
  const emailValid = !!email && !formState.errors.email;
  const canGoNext =
    !!companyId && modulesStatus === 'ready' && !checkingStaff && (isExisting ? !!pickedUser : emailValid);

  const goNext = async () => {
    if (!canGoNext || !companyId) return;
    const subjectKey = isExisting ? `user:${pickedUser?.user_id}` : `email:${email.toLowerCase()}`;
    if (seededForRef.current === subjectKey) {
      setStep('role');
      return;
    }

    let found: StaffRow | undefined;
    let overrides: ModulePermissionOverridesDTO[] = [];
    if (isExisting && pickedUser) {
      try {
        setCheckingStaff(true);
        found = await findStaffMember(getStaffRequest, companyId, pickedUser.user_id, pickedUser.last_name);
        // An admin already may have fine-tuned permissions — start from those, so a
        // save here can't quietly drop them
        if (found && !found.owner && catalogue) {
          overrides = await loadOverrides(companyId, pickedUser.user_id);
        }
      } catch (e) {
        handleServerError(e);
        return;
      } finally {
        setCheckingStaff(false);
      }
      if (found?.owner) {
        showNotification({
          message: `${pickedUser.first_name} owns this company`,
          description: 'Owners already have full access to every module.',
          options: { variant: 'info' },
        });
        return;
      }
    }

    // Someone already on the staff list starts from the roles they hold, so saving
    // here never quietly strips access they had
    const seededDraft = draftFromStaffItem(modules, found);
    const overridesByModule = new Map(overrides.map(item => [item.module_system_name, item]));
    const seededPermissions: PermissionDraft = {};
    modules.forEach(module => {
      const role = seededDraft[module.systemName]?.role || null;
      const dto = overridesByModule.get(module.systemName);
      const state =
        dto && catalogue && role && !isFullAccessRole(role)
          ? permissionStateFromDTO(dto, catalogue)
          : permissionStateForRole(module, role);
      if (state) seededPermissions[module.systemName] = state;
    });
    setStaffItem(found ?? null);
    setPosition(found?.position_name || '');
    setBaseline({ draft: seededDraft, permissions: seededPermissions });
    setCustomising({});
    resetInviteCustomise();
    // A prepared invite is for someone new, so there is nothing to keep: its roles
    // replace the empty start and the ticks start from those roles' defaults
    if (!isExisting && prefill && prefill.email.toLowerCase() === email.toLowerCase()) {
      const prefilledDraft = draftFromPrefill(modules, prefill);
      const prefilledPermissions: PermissionDraft = {};
      modules.forEach(module => {
        const state = permissionStateForRole(module, prefilledDraft[module.systemName]?.role || null);
        if (state) prefilledPermissions[module.systemName] = state;
      });
      setPosition(prefill.position);
      setDraft(prefilledDraft);
      setPermissions(prefilledPermissions);
    } else {
      setDraft(seededDraft);
      setPermissions(seededPermissions);
    }
    seededForRef.current = subjectKey;
    setStep('role');
  };

  // The prepared invite goes straight to its roles, once the modules are in
  useEffect(() => {
    if (prefill && step === 'who' && canGoNext && !seededForRef.current) {
      goNext();
    }
    // goNext only changes with the render; the readiness decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, step, canGoNext]);

  const goBack = () => setStep('who');

  const togglePickedUser = (worker: CompanyWorkerItem) =>
    setPickedUser(current => (current?.user_id === worker.user_id ? null : worker));

  const setModuleRole = (moduleKey: string, role: CompanyRoleDTO | null) => {
    const reassigned = getRoleReassignments(draft, moduleKey, role, modules);
    setDraft(prev => applyRoleReassignments(prev, reassigned, modules, staffItem?.resources || {}));

    // The ticks belong to the role: a new role starts from its own set, and going
    // back to the role the person already holds restores what they had
    const nextPermissions = { ...permissions };
    const nextCustomising = { ...customising };
    reassigned.forEach((nextRole, systemName) => {
      const module = modules.find(item => item.systemName === systemName);
      const savedRoleId = baseline.draft[systemName]?.role?.role_id || null;
      const savedState = baseline.permissions[systemName];
      const state =
        nextRole && savedState && nextRole.role_id === savedRoleId
          ? { ...savedState, conflicts: [] }
          : module && permissionStateForRole(module, nextRole);
      if (state) {
        nextPermissions[systemName] = state;
      } else {
        delete nextPermissions[systemName];
        delete nextCustomising[systemName];
      }
    });
    setPermissions(nextPermissions);
    setCustomising(nextCustomising);
  };

  const toggleCustomising = (moduleKey: string) => {
    if (customisesInvite) setCustomiseOpened(true);
    setCustomising(prev => ({ ...prev, [moduleKey]: !prev[moduleKey] }));
  };

  const togglePermission = (moduleKey: string, systemName: string, checked: boolean) => {
    setInviteError('');
    setPermissions(prev => {
      const current = prev[moduleKey];
      if (!current) return prev;
      return {
        ...prev,
        [moduleKey]: {
          ...current,
          explicit: toggleId(current.explicit, systemName, checked),
          conflicts: [],
        },
      };
    });
  };

  // Ticks the role's own set; saved like that, the server drops the override
  const resetModulePermissions = (moduleKey: string) => {
    setInviteError('');
    setPermissions(prev => {
      const current = prev[moduleKey];
      if (!current || !catalogue) return prev;
      return {
        ...prev,
        [moduleKey]: {
          ...current,
          explicit: Array.from(explicitFromEffective(current.roleDefaults, catalogue.index)),
          conflicts: [],
        },
      };
    });
  };

  // Scoped to each module's own names — implied_permissions cross modules, while
  // the server's role_defaults and effective sets are per module
  const effectiveByModule = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    if (!catalogue) return result;
    modules.forEach(module => {
      const state = permissions[module.systemName];
      if (state) {
        result[module.systemName] = ownPermissions(module, closure(state.explicit, catalogue.index));
      }
    });
    return result;
  }, [permissions, modules, catalogue]);

  const normalise = useCallback(
    (module: VisibleStaffModule, names: string[]) =>
      catalogue ? ownPermissions(module, closure(names, catalogue.index)) : new Set(names),
    [catalogue]
  );

  /** Modules whose ticks differ from what their role gives */
  const customisedModules = useMemo(
    () =>
      modules
        .filter(module => {
          const state = permissions[module.systemName];
          const effective = effectiveByModule[module.systemName];
          return !!state && !!effective && !setsEqual(effective, normalise(module, state.roleDefaults));
        })
        .map(module => module.systemName),
    [modules, permissions, effectiveByModule, normalise]
  );

  // What the overrides PUT has to carry. Same rule as the admins list: a module
  // whose role changed only needs it when the ticks differ from the new role's
  // defaults (saving the role resets them); otherwise when they moved from what
  // the person had.
  const dirtyPermissionModules = useMemo(
    () =>
      modules
        .filter(module => {
          const state = permissions[module.systemName];
          const effective = effectiveByModule[module.systemName];
          if (!state || !effective) return false;
          const savedRoleId = baseline.draft[module.systemName]?.role?.role_id || null;
          const savedState = baseline.permissions[module.systemName];
          const roleChanged = draft[module.systemName]?.role?.role_id !== savedRoleId;
          if (roleChanged || !savedState) {
            return !setsEqual(effective, normalise(module, state.roleDefaults));
          }
          return !setsEqual(effective, normalise(module, savedState.baselineEffective));
        })
        .map(module => module.systemName),
    [modules, permissions, effectiveByModule, baseline, draft, normalise]
  );

  // A 409 names the "Y requires X" pairs; each goes to the modules it mentions, and
  // those panels open so the pair is in view
  const showConflicts = (conflicts: PermissionRequirementPair[]) => {
    const touched = modules
      .filter(module => {
        const own = new Set(module.permissions.map(permission => permission.system_name));
        return conflicts.some(pair => own.has(pair.permission) || own.has(pair.requires));
      })
      .map(module => module.systemName);
    setPermissions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(systemName => {
        const own = new Set(
          (modules.find(module => module.systemName === systemName)?.permissions || []).map(
            permission => permission.system_name
          )
        );
        const mine = conflicts.filter(pair => own.has(pair.permission) || own.has(pair.requires));
        if (mine.length || next[systemName].conflicts.length) {
          next[systemName] = { ...next[systemName], conflicts: mine };
        }
      });
      return next;
    });
    setCustomising(prev => {
      const next = { ...prev };
      touched.forEach(systemName => {
        next[systemName] = true;
      });
      return next;
    });
    return touched.length > 0;
  };

  // The 409 / 422s the preview and the invite share. Returns the line to show by the
  // form, or '' when the error was answered some other way.
  const handleInviteOverridesError = async (e: any): Promise<string> => {
    const status = e?.response?.status;
    if (status === 409) {
      const conflicts = parsePermissionConflicts(e);
      if (conflicts.length && showConflicts(conflicts)) return '';
      return errorDescription(e) || 'Some of the unticked permissions are still needed by ticked ones.';
    }
    if (status === 422 && isInvalidRoleError(e) && companyId) {
      // A role was deleted since the picker loaded: reload it and drop the role
      invalidateCompanyRolesData(companyId);
      const fresh = await loadModules();
      if (fresh) {
        setDraft(prev => {
          const next = { ...prev };
          fresh.forEach(module => {
            const role = next[module.systemName]?.role;
            if (role && !module.roles.some(item => item.role_id === role.role_id)) {
              next[module.systemName] = { role: null, resourceIds: [] };
            }
          });
          return next;
        });
      }
      return "One of these roles no longer exists. We've reloaded the list — pick the roles again.";
    }
    if (status === 422 && isUnavailableModuleError(e)) {
      // The rows went stale against the roles — draw them again
      setInviteRowsReload(value => value + 1);
      return 'The permission list was out of date and has been reloaded. Check the ticks and try again.';
    }
    if (status === 422) {
      return (
        [errorTitle(e), errorDescription(e)].filter(Boolean).join(' ') || "Those permissions can't be used."
      );
    }
    return '';
  };

  // Step 1 of the preview: the rows for the roles picked, drawn again whenever the
  // roles change. A module keeps its ticks as long as its defaults didn't move.
  useEffect(() => {
    if (
      !customisesInvite ||
      !customiseOpened ||
      !companyId ||
      !catalogue ||
      !previewPermissionOverridesRequest
    ) {
      return;
    }
    const requestId = ++inviteRowsRequestRef.current;
    if (!roleIds.length) {
      setInviteRows([]);
      setInviteRowsStatus('ready');
      return;
    }
    setInviteRowsStatus('loading');
    previewPermissionOverridesRequest(companyId, { roles: roleIds.map(role_id => ({ role_id })) })
      .then(res => {
        if (requestId !== inviteRowsRequestRef.current) return;
        const rows = res.data || [];
        setInviteRows(rows);
        setPermissions(prev => {
          const next: PermissionDraft = {};
          rows.forEach(row => {
            const fresh = permissionStateFromDTO(row, catalogue);
            const current = prev[row.module_system_name];
            next[row.module_system_name] =
              current && setsEqual(new Set(current.roleDefaults), new Set(fresh.roleDefaults))
                ? { ...fresh, explicit: current.explicit, conflicts: current.conflicts }
                : fresh;
          });
          return next;
        });
        setInviteError('');
        setInviteRowsStatus('ready');
      })
      .catch(async e => {
        if (requestId !== inviteRowsRequestRef.current) return;
        setInviteRowsStatus('error');
        const message = await handleInviteOverridesError(e);
        if (message) setInviteError(message);
        else if (e?.response?.status !== 409) handleServerError(e);
      });
    // The roles are read through rolesKey; the handlers only change with the render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customisesInvite, customiseOpened, companyId, catalogue, rolesKey, inviteRowsReload]);

  const inviteRowsReady =
    customisesInvite && customiseOpened && inviteRowsStatus === 'ready' && !!inviteRows;

  // permission_overrides: one entry per row step 1 drew, each the full ticked list in
  // catalogue order. Rows that only show up in step 2 are never sent — the invite
  // adds those dependencies itself.
  const inviteOverrides = useMemo<InviteModulePermissionOverride[]>(() => {
    if (!inviteRowsReady || !inviteRows) return [];
    return inviteRows.map(row => {
      const module = modules.find(item => item.systemName === row.module_system_name);
      const effective = effectiveByModule[row.module_system_name];
      if (!module || !effective) {
        return { module_system_name: row.module_system_name, permissions: row.effective };
      }
      const shown = new Set(module.permissions.map(permission => permission.system_name));
      return {
        module_system_name: row.module_system_name,
        permissions: [
          ...module.permissions
            .map(permission => permission.system_name)
            .filter(name => effective.has(name)),
          // Anything the form can't draw stays as the role gives it
          ...row.effective.filter(name => !shown.has(name)),
        ],
      };
    });
  }, [inviteRowsReady, inviteRows, modules, effectiveByModule]);
  const inviteOverridesKey = useMemo(() => JSON.stringify(inviteOverrides), [inviteOverrides]);
  const inviteCustomised = inviteRowsReady && customisedModules.length > 0;

  // Step 2 of the preview, once the ticks sit still: the invite's own checks, so
  // the boxes it will add — and any pair it refuses — show before it is sent
  useEffect(() => {
    if (!inviteCustomised || !companyId || !previewPermissionOverridesRequest) {
      inviteCheckRequestRef.current += 1;
      setInviteCheck(null);
      return;
    }
    const requestId = ++inviteCheckRequestRef.current;
    const timer = setTimeout(async () => {
      try {
        const res = await previewPermissionOverridesRequest(companyId, {
          roles: roleIds.map(role_id => ({ role_id })),
          permissions: inviteOverrides,
        });
        if (requestId !== inviteCheckRequestRef.current) return;
        setInviteCheck({ key: inviteOverridesKey, rows: res.data || [] });
        setInviteError('');
        // The ticks pass as they stand, so no pair from an earlier answer still holds
        setPermissions(prev =>
          Object.values(prev).some(state => state.conflicts.length)
            ? Object.fromEntries(
                Object.entries(prev).map(([systemName, state]) => [systemName, { ...state, conflicts: [] }])
              )
            : prev
        );
      } catch (e: any) {
        if (requestId !== inviteCheckRequestRef.current) return;
        setInviteCheck(null);
        const message = await handleInviteOverridesError(e);
        if (message) setInviteError(message);
        else if (e?.response?.status !== 409) handleServerError(e);
      }
    }, INVITE_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
    // The ticks are read through inviteOverridesKey and the roles through rolesKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCustomised, companyId, rolesKey, inviteOverridesKey]);

  const currentInviteCheck = inviteCheck && inviteCheck.key === inviteOverridesKey ? inviteCheck.rows : null;

  // What the invite will tick on top of the form: per module the form draws, and
  // read-only rows for modules the roles don't cover at all
  const { autoAddedByModule, autoAddedRows } = useMemo(() => {
    const byModule: Record<string, string[]> = {};
    const extra: AutoAddedRow[] = [];
    if (!currentInviteCheck || !inviteRows) return { autoAddedByModule: byModule, autoAddedRows: extra };
    const drawn = new Set(inviteRows.map(row => row.module_system_name));
    currentInviteCheck.forEach(row => {
      if (drawn.has(row.module_system_name)) {
        const module = modules.find(item => item.systemName === row.module_system_name);
        const shown = new Set((module?.permissions || []).map(permission => permission.system_name));
        const effective = effectiveByModule[row.module_system_name] || new Set<string>();
        const added = row.effective.filter(name => shown.has(name) && !effective.has(name));
        if (added.length) byModule[row.module_system_name] = added;
      } else if (row.effective.length) {
        extra.push({
          systemName: row.module_system_name,
          label: row.module_display_name,
          permissions: row.effective,
        });
      }
    });
    return { autoAddedByModule: byModule, autoAddedRows: extra };
  }, [currentInviteCheck, inviteRows, modules, effectiveByModule]);

  const hasConflicts = Object.values(permissions).some(state => state.conflicts.length > 0);
  const checkingInvite = inviteCustomised && !currentInviteCheck && !inviteError && !hasConflicts;

  /** Whether a module row offers Customise — on an invite, any row the preview drew */
  const isModuleCustomisable = (module: VisibleStaffModule) => {
    if (!canCustomise || !catalogue || !module.permissions.length || !permissions[module.systemName]) {
      return false;
    }
    if (draft[module.systemName]?.role) return true;
    return customisesInvite && !!inviteRows?.some(row => row.module_system_name === module.systemName);
  };

  const toggleModuleResource = (moduleKey: string, resourceId: string, checked: boolean) =>
    setDraft(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        resourceIds: toggleId(prev[moduleKey]?.resourceIds || [], resourceId, checked),
      },
    }));

  const toggleAllModuleResources = (moduleKey: string, checked: boolean) => {
    const module = modules.find(item => item.systemName === moduleKey);
    setDraft(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        resourceIds: checked ? (module?.resources || []).map(item => item.id) : [],
      },
    }));
  };

  const invalidReason = useMemo(() => getDraftInvalidReason(draft, modules), [draft, modules]);
  const assignedCount = modules.filter(module => !!draft[module.systemName]?.role).length;
  // Once Customise is opened on an invite, wait for the rows it draws — and go
  // nowhere while a pair is still in conflict. If the rows never came, nothing could
  // be ticked, so the invite goes without the field: role defaults, as shown.
  const inviteCustomiseBlocked =
    customisesInvite && customiseOpened && (inviteRowsStatus === 'loading' || hasConflicts);
  const canSubmit =
    !!companyId &&
    !!position.trim() &&
    !invalidReason &&
    assignedCount > 0 &&
    !saving &&
    !inviteCustomiseBlocked;

  const personName = pickedUser ? `${pickedUser.first_name} ${pickedUser.last_name}`.trim() : email;

  const saveCustomPermissions = async (userId: string) => {
    if (!companyId || !dirtyPermissionModules.length) return;
    const payload: UpdateModulePermissionOverridePayload[] = dirtyPermissionModules.map(systemName => {
      const module = modules.find(item => item.systemName === systemName);
      const effective = effectiveByModule[systemName] || new Set<string>();
      return {
        module_system_name: systemName,
        // Always the full ticked state, in catalogue order — never [] as a shorthand
        // for "reset", which the server reads as "remove every permission"
        permissions: (module?.permissions || [])
          .map(permission => permission.system_name)
          .filter(name => effective.has(name)),
        clear_resource_overrides: true,
      };
    });
    await updateUserPermissionOverridesRequest(companyId, userId, payload);
  };

  const submit = async () => {
    if (!canSubmit || !companyId) return;
    const { roles, emptyScopeLabels } = buildRolesPayload(draft, modules, companyId);
    const positionName = position.trim();
    let rolesSaved = false;
    try {
      setSaving(true);
      if (isExisting && pickedUser) {
        await updateStaffPermissionsRequest(companyId, pickedUser.user_id, {
          position_name: positionName,
          roles,
        });
        rolesSaved = true;
        // Overrides are stored against a position, so they can only go in once the
        // roles above have landed
        await saveCustomPermissions(pickedUser.user_id);
        showNotification({
          message: `${personName} now has admin access`,
          description: customisedModules.length
            ? "Custom permissions included. They'll see it the next time they refresh Vyce."
            : "They'll see it the next time they refresh Vyce.",
          options: { variant: 'success' },
        });
      } else {
        const payload: SendStaffInviteRequest = {
          email,
          staff_payload: { position_name: positionName, roles },
        };
        // Only when Customise was opened: no field means role defaults, while a
        // list — even of defaults — replaces what the person may already have here
        if (inviteRowsReady) payload.permission_overrides = inviteOverrides;
        await createStaffInviteRequest(companyId, payload);
        showNotification({
          message: `Invite sent to ${email}`,
          description: inviteCustomised ? 'Custom permissions included.' : undefined,
          options: { variant: 'success' },
        });
      }
      onSuccess();
      handleClose();
    } catch (e: any) {
      const resolveLabel = (systemName: string) =>
        catalogue ? toDisplayName(systemName, catalogue.index) : systemName;
      if (!isExisting && inviteRowsReady) {
        // The invite ran the same checks as the preview: answer them by the form
        const status = e?.response?.status;
        if (status === 409 || status === 422) {
          const message = await handleInviteOverridesError(e);
          setInviteError(message);
          if (!message && status === 409) {
            showNotification({
              message: "The invite wasn't sent",
              description:
                'Some unticked permissions are still needed by ticked ones — see the highlighted pairs.',
              options: { variant: 'error' },
            });
          }
          return;
        }
      }
      if (rolesSaved) {
        // The person has their roles; only the fine-tuning failed. Keep the dialog
        // open with the ticks as they were, so it can be fixed and saved again.
        onSuccess();
        const conflicts = e?.response?.status === 409 ? parsePermissionConflicts(e) : [];
        if (conflicts.length) {
          setPermissions(prev => {
            const next = { ...prev };
            dirtyPermissionModules.forEach(systemName => {
              if (next[systemName]) next[systemName] = { ...next[systemName], conflicts };
            });
            return next;
          });
          setCustomising(prev => {
            const next = { ...prev };
            dirtyPermissionModules.forEach(systemName => {
              next[systemName] = true;
            });
            return next;
          });
        }
      }
      const notification = getSaveErrorNotification(e, resolveLabel, rolesSaved ? [] : emptyScopeLabels);
      if (rolesSaved && !notification) {
        showNotification({
          message: `${personName} has their roles, but the custom permissions weren't saved`,
          description: 'Check the ticked permissions and try again.',
          options: { variant: 'error' },
        });
      } else if (notification) {
        showNotification(notification);
      } else {
        handleServerError(e);
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    methods,
    isMobile,
    companyId,
    setCompanyId,
    step,
    tab,
    setTab,
    email,
    pickedUser,
    togglePickedUser,
    staffItem,
    personName,
    isExisting,
    position,
    setPosition,
    modules,
    modulesStatus,
    retryModules: loadModules,
    draft,
    assignedCount,
    invalidReason,
    setModuleRole,
    catalogue,
    canCustomise,
    hasOwnerRole,
    customisesInvite,
    isModuleCustomisable,
    inviteRowsStatus,
    retryInviteRows: () => setInviteRowsReload(value => value + 1),
    checkingInvite,
    inviteError,
    autoAddedByModule,
    autoAddedRows,
    permissions,
    effectiveByModule,
    customisedModules,
    customising,
    toggleCustomising,
    togglePermission,
    resetModulePermissions,
    toggleModuleResource,
    toggleAllModuleResources,
    checkingStaff,
    canGoNext,
    goNext,
    goBack,
    canSubmit,
    saving,
    submit,
    handleClose,
  };
};

export type AdminInviteDialogApi = ReturnType<typeof useAdminInviteDialog>;
